import React, { useEffect, useState } from "react";
import axios from "axios";

const colors = {
  mainBlue: "#061941",
  xanthous: "#F2AF29",
  isabelline: "#F4F3EE",
  carrieban: "#3C6E71",
  muted: "#6B7280",
};

const notificationIcons = {
  application_accepted: "✓",
  application_rejected: "✕",
  application_forwarded: "→",
};

const notificationColors = {
  application_accepted: colors.carrieban,
  application_rejected: "#DC2626",
  application_forwarded: colors.xanthous,
};

function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const userData = JSON.parse(localStorage.getItem("user")) || {};
  const token = userData.token;

  const fetchNotifications = async (isInitial = false) => {
    try {
      if (isInitial) setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unreadCount || 0);
    } catch (err) {
      console.error("Error fetching notifications:", err);
    } finally {
      if (isInitial) setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchNotifications(true);
      const interval = setInterval(() => fetchNotifications(false), 3000);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleMarkAsRead = async (notificationId) => {
    try {
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications/${notificationId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchNotifications();
    } catch (err) {
      console.error("Error marking notification as read:", err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications/read-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchNotifications();
    } catch (err) {
      console.error("Error marking all as read:", err);
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications/${notificationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchNotifications();
    } catch (err) {
      console.error("Error deleting notification:", err);
    }
  };

  const formatTime = (date) => {
    const now = new Date();
    const notifDate = new Date(date);
    const diffMs = now - notifDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return notifDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year:
        notifDate.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 80px)",
        backgroundColor: colors.isabelline,
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "24px 20px",
          backgroundColor: "white",
          borderBottom: `1px solid ${colors.mainBlue}20`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <h1
            style={{
              margin: 0,
              color: colors.mainBlue,
              fontSize: "28px",
              fontWeight: 700,
            }}
          >
            Notifications
          </h1>
          <p
            style={{
              margin: "8px 0 0 0",
              color: colors.muted,
              fontSize: "14px",
            }}
          >
            {unreadCount > 0 ? `${unreadCount} unread` : "All caught up"}
          </p>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllAsRead}
            style={{
              padding: "8px 16px",
              backgroundColor: colors.mainBlue,
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "13px",
              fontWeight: 600,
            }}
          >
            Mark all as read
          </button>
        )}
      </div>

      {/* Notifications List */}
      <div style={{ flex: 1, overflowY: "auto" }}>
        {loading ? (
          <div
            style={{
              padding: "40px 20px",
              textAlign: "center",
              color: colors.muted,
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                border: `3px solid ${colors.mainBlue}20`,
                borderTop: `3px solid ${colors.mainBlue}`,
                borderRadius: "50%",
                margin: "0 auto 16px",
                animation: "spin 1s linear infinite",
              }}
            />
            <p>Loading notifications...</p>
            <style>{`
              @keyframes spin {
                to { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        ) : notifications.length === 0 ? (
          <div
            style={{
              padding: "60px 20px",
              textAlign: "center",
              color: colors.muted,
            }}
          >
            <svg
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              style={{ margin: "0 auto 16px", opacity: 0.5 }}
            >
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            <h3
              style={{
                margin: "0 0 8px 0",
                fontSize: "16px",
                fontWeight: 600,
                color: colors.mainBlue,
              }}
            >
              No notifications yet
            </h3>
            <p style={{ margin: 0, fontSize: "14px" }}>
              You'll see notifications about your applications here
            </p>
          </div>
        ) : (
          <div>
            {notifications.map((notification) => (
              <div
                key={notification._id}
                onClick={() =>
                  !notification.read && handleMarkAsRead(notification._id)
                }
                style={{
                  padding: "16px 20px",
                  borderBottom: `1px solid ${colors.mainBlue}10`,
                  cursor: notification.read ? "default" : "pointer",
                  backgroundColor: notification.read
                    ? "white"
                    : colors.isabelline,
                  transition: "background-color 0.2s",
                }}
                onMouseOver={(e) => {
                  if (!notification.read) {
                    e.currentTarget.style.backgroundColor =
                      colors.mainBlue + "05";
                  }
                }}
                onMouseOut={(e) => {
                  if (!notification.read) {
                    e.currentTarget.style.backgroundColor = colors.isabelline;
                  }
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    alignItems: "flex-start",
                  }}
                >
                  {/* Icon */}
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      backgroundColor: notificationColors[notification.type],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: "20px",
                      fontWeight: "bold",
                      flexShrink: 0,
                    }}
                  >
                    {notificationIcons[notification.type]}
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "baseline",
                        marginBottom: "4px",
                      }}
                    >
                      <h3
                        style={{
                          margin: 0,
                          fontSize: "15px",
                          fontWeight: 600,
                          color: colors.mainBlue,
                        }}
                      >
                        {notification.title}
                      </h3>
                      <span
                        style={{
                          fontSize: "12px",
                          color: colors.muted,
                          marginLeft: "8px",
                          flexShrink: 0,
                        }}
                      >
                        {formatTime(notification.createdAt)}
                      </span>
                    </div>

                    {/* Message */}
                    <p
                      style={{
                        margin: "4px 0 0 0",
                        fontSize: "14px",
                        color: colors.muted,
                        lineHeight: "1.5",
                      }}
                    >
                      {notification.message}
                    </p>

                    {/* Sender Info */}
                    {notification.sender && (
                      <p
                        style={{
                          margin: "6px 0 0 0",
                          fontSize: "12px",
                          color: colors.muted,
                          opacity: 0.7,
                        }}
                      >
                        From:{" "}
                        {notification.isSystemMessage
                          ? "admin"
                          : notification.sender.name}
                      </p>
                    )}
                  </div>

                  {/* Delete Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(notification._id);
                    }}
                    style={{
                      padding: "4px 8px",
                      backgroundColor: "transparent",
                      color: colors.muted,
                      border: "none",
                      cursor: "pointer",
                      fontSize: "16px",
                      flexShrink: 0,
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.color = colors.mainBlue;
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.color = colors.muted;
                    }}
                  >
                    ✕
                  </button>
                </div>

                {/* Unread Indicator */}
                {!notification.read && (
                  <div
                    style={{
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      backgroundColor: colors.mainBlue,
                      position: "absolute",
                      left: "8px",
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info Footer */}
      {notifications.length > 0 && (
        <div
          style={{
            padding: "16px 20px",
            backgroundColor: "white",
            borderTop: `1px solid ${colors.mainBlue}10`,
            fontSize: "12px",
            color: colors.muted,
            textAlign: "center",
          }}
        >
          Click on a notification to mark it as read
        </div>
      )}
    </div>
  );
}

export default Notifications;
